import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAccount } from "wagmi";

interface WithdrawalRecord {
  id: number;
  amount: string;
  status: "PENDING_REVIEW" | "APPROVED" | "EXECUTED" | "REJECTED";
  requestedAt: string;
  processedAt: string | null;
  txHash: string | null;
}

const STATUS_STYLE: Record<string, string> = {
  PENDING_REVIEW: "text-yellow-400 bg-yellow-900/30",
  APPROVED: "text-blue-400 bg-blue-900/30",
  EXECUTED: "text-green-400 bg-green-900/30",
  REJECTED: "text-red-400 bg-red-900/30",
};

export function WalletPage() {
  const { address, isConnected } = useAccount();
  const [balance, setBalance] = useState<string>("0");
  const [depositAmount, setDepositAmount] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawals, _setWithdrawals] = useState<WithdrawalRecord[]>([]);
  const [activeTab, setActiveTab] = useState<"deposit" | "withdraw">("deposit");

  useEffect(() => {
    if (isConnected && address) {
      // TODO: Read balance from CrownVault contract
      // const bal = await vaultContract.balanceOf(address);
      setBalance("0.00");
    }
  }, [isConnected, address]);

  if (!isConnected) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <h2 className="text-2xl font-bold mb-6">Wallet</h2>
        <div className="text-center py-12 bg-gray-900 rounded-lg">
          <p className="text-gray-400 text-lg">Connect your wallet to manage funds</p>
          <Link to="/" className="text-blue-400 hover:text-blue-300 mt-2 inline-block">
            Go to Home
          </Link>
        </div>
      </div>
    );
  }

  async function handleDeposit() {
    if (!depositAmount || parseFloat(depositAmount) <= 0) return;

    // TODO: 1. Approve USDT to vault
    // const usdtContract = getContract({ address: CONTRACTS.USDT, abi: erc20Abi });
    // await usdtContract.write.approve([CONTRACTS.CROWN_VAULT, parseEther(depositAmount)]);
    // 2. Call vault.deposit(amount)
    alert(`Deposit ${depositAmount} USDT — wallet transaction pending`);
  }

  async function handleWithdraw() {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) return;

    // TODO: Call vault.requestWithdrawal(amount)
    // If amount > 50,000 USDT → goes to PENDING_REVIEW for multisig approval
    alert(`Withdraw ${withdrawAmount} USDT — wallet transaction pending`);
  }

  return (
    <div className="p-8 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Wallet</h2>
        <Link to="/matches" className="text-blue-400 hover:text-blue-300 text-sm">
          Browse Matches
        </Link>
      </div>

      {/* Balance card */}
      <div className="bg-gray-900 rounded-lg border border-gray-800 p-6 mb-6">
        <p className="text-sm text-gray-500 mb-1">Vault Balance</p>
        <p className="text-3xl font-bold font-mono">{balance} USDT</p>
        <p className="text-xs text-gray-500 mt-2">
          Internal balance — used for betting. Withdraw anytime.
        </p>
      </div>

      {/* Deposit / Withdraw tabs */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveTab("deposit")}
          className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
            activeTab === "deposit"
              ? "bg-green-600 text-white"
              : "bg-gray-800 text-gray-400 hover:bg-gray-700"
          }`}
        >
          Deposit
        </button>
        <button
          onClick={() => setActiveTab("withdraw")}
          className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
            activeTab === "withdraw"
              ? "bg-red-600 text-white"
              : "bg-gray-800 text-gray-400 hover:bg-gray-700"
          }`}
        >
          Withdraw
        </button>
      </div>

      {activeTab === "deposit" ? (
        <div className="bg-gray-900 rounded-lg border border-gray-800 p-6">
          <h3 className="font-semibold mb-4">Deposit USDT</h3>
          <p className="text-sm text-gray-400 mb-4">
            Deposit USDT from your wallet into the Crown Vault. Requires ERC-20
            approval first.
          </p>
          <div className="flex gap-3">
            <input
              type="number"
              min="1"
              step="1"
              placeholder="Amount"
              value={depositAmount}
              onChange={(e) => setDepositAmount(e.target.value)}
              className="flex-1 bg-gray-800 border border-gray-700 rounded px-4 py-3 text-white focus:border-green-500 focus:outline-none"
            />
            <button
              onClick={handleDeposit}
              disabled={!depositAmount || parseFloat(depositAmount) <= 0}
              className="px-8 py-3 bg-green-600 rounded-lg font-semibold hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Deposit
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-gray-900 rounded-lg border border-gray-800 p-6">
          <h3 className="font-semibold mb-4">Withdraw USDT</h3>
          <p className="text-sm text-gray-400 mb-4">
            Withdraw USDT from the vault to your wallet. Amounts over 50,000 USDT
            require multisig approval.
          </p>
          <div className="flex gap-3">
            <input
              type="number"
              min="1"
              step="1"
              placeholder="Amount"
              value={withdrawAmount}
              onChange={(e) => setWithdrawAmount(e.target.value)}
              className="flex-1 bg-gray-800 border border-gray-700 rounded px-4 py-3 text-white focus:border-red-500 focus:outline-none"
            />
            <button
              onClick={handleWithdraw}
              disabled={!withdrawAmount || parseFloat(withdrawAmount) <= 0}
              className="px-8 py-3 bg-red-600 rounded-lg font-semibold hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Withdraw
            </button>
          </div>
          {parseFloat(withdrawAmount || "0") > 50000 && (
            <p className="text-xs text-yellow-400 mt-3">
              Large withdrawal: requires multisig approval (may take up to 24h)
            </p>
          )}
        </div>
      )}

      {/* Withdrawal history */}
      {withdrawals.length > 0 && (
        <div className="mt-6">
          <h3 className="font-semibold mb-3">Withdrawal History</h3>
          <div className="space-y-2">
            {withdrawals.map((w) => (
              <div
                key={w.id}
                className="bg-gray-900 rounded-lg border border-gray-800 p-3 flex items-center justify-between"
              >
                <div>
                  <span className="font-mono text-sm">
                    {parseFloat(w.amount).toFixed(2)} USDT
                  </span>
                  <span className="text-xs text-gray-500 ml-2">
                    {new Date(w.requestedAt).toLocaleDateString()}
                  </span>
                </div>
                <span
                  className={`text-xs px-2 py-1 rounded ${STATUS_STYLE[w.status] || ""}`}
                >
                  {w.status.replace("_", " ")}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
